<template>
    <div :style="rootStyle">
        <!-- Title row: H1 + count + stats + actions -->
        <div :style="titleRowStyle">
            <div :style="titleColStyle">
                <div :style="titleLineStyle">
                    <h1 :style="h1Style">Agencies</h1>
                    <span class="tag-mono" :style="countStyle">{{ totalItems }}</span>
                </div>
                <div :style="statsStyle">
                    <slot name="stats">
                        <!-- Caller can pass live counts; the default keeps a
                             muted "—" placeholder so the row never collapses. -->
                        <span :style="statSpanStyle">Active <b class="tag-mono tag-tnum" :style="statValueStyle">—</b></span>
                    </slot>
                </div>
            </div>
            <div :style="actionsStyle">
                <a
                    v-if="exportHref"
                    :href="exportHref"
                    class="btn btn--ghost btn--sm"
                    :style="{ gap: '5px' }"
                >
                    <Icon name="download" />
                    Export Excel
                </a>
                <button
                    type="button"
                    class="btn btn--subtle btn--sm"
                    :style="{ gap: '5px' }"
                    @click="$emit('edit-columns')"
                >
                    <Icon name="columns" />
                    Edit columns
                </button>
                <a
                    v-if="addHref"
                    :href="addHref"
                    class="btn btn--primary btn--sm"
                    :style="{ gap: '5px' }"
                >
                    <Icon name="plus" />
                    Add agency
                </a>
            </div>
        </div>

        <!-- Search + facet chips -->
        <div :style="facetRowStyle">
            <div :style="searchWrapStyle">
                <span :style="searchIconStyle"><Icon name="search" /></span>
                <input
                    class="input"
                    placeholder="Search agencies…"
                    :style="searchInputStyle"
                    :value="searchValue"
                    @input="onSearchInput"
                />
                <span :style="searchKbdStyle">
                    <span class="kbd">⌘</span>
                    <span class="kbd">F</span>
                </span>
            </div>

            <!-- Inline facet chips — variant A's primary filter affordance.
                 Each chip opens the FilterRail sheet on the right (the parent
                 handles wiring via @toggle-filters). Clearing an active facet
                 hands the slug back to the parent via @clear-facet. -->
            <template v-if="variant === 'top'">
                <FacetButton
                    v-for="f in facets"
                    :key="f.slug"
                    :label="f.label"
                    :value="f.value"
                    :active="f.active"
                    @click="$emit('toggle-filters')"
                    @clear="$emit('clear-facet', f.slug)"
                />
            </template>

            <slot name="facets" />
            <span :style="{ flex: '1 1 auto' }" />

            <!-- More filters button — opens the FilterRail sheet in variant A.
                 Variant B keeps the rail always-on, so this button hides. -->
            <button
                v-if="variant === 'top'"
                type="button"
                class="btn btn--sm"
                :style="moreFiltersBtnStyle"
                @click="$emit('toggle-filters')"
            >
                <Icon name="filter" />
                More filters
                <span
                    v-if="activeFilterCount > 0"
                    class="tag-mono"
                    :style="moreFiltersBadgeStyle"
                >{{ activeFilterCount }}</span>
            </button>

            <button class="btn btn--sm btn--ghost" type="button" :style="{ gap: '5px' }">
                <Icon name="sort" />
                Sort
            </button>
        </div>

        <!-- Status segmented control -->
        <div :style="statusRowStyle">
            <span :style="statusEyebrowStyle">Status</span>
            <button
                type="button"
                class="btn btn--xs"
                :style="statusTabStyle(selectedStatuses.length === 0)"
                @click="onStatusClick(null)"
            >
                All
                <span class="tag-mono" :style="statusCountStyle(selectedStatuses.length === 0)">
                    {{ totalItems }}
                </span>
            </button>
            <button
                v-for="s in statusOptions"
                :key="s.id"
                type="button"
                class="btn btn--xs"
                :style="statusTabStyle(selectedStatuses.includes(s.id))"
                @click="onStatusClick(s.id)"
            >
                {{ s.label }}
                <span
                    v-if="s.count != null"
                    class="tag-mono"
                    :style="statusCountStyle(selectedStatuses.includes(s.id))"
                >{{ s.count }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import Icon from "./Icon.vue";
import FacetButton from "./FacetButton.vue";

export default {
    name: "AgenciesHeader",
    components: { Icon, FacetButton },
    props: {
        totalItems: { type: [Number, String], default: 0 },
        searchValue: { type: String, default: "" },
        // ARRAY of selected status ids (matches the parent's
        // filters.agencies_status — multi-select segmented control).
        selectedStatuses: { type: Array, default: () => [] },
        statusOptions: { type: Array, default: () => [] },
        exportHref: { type: String, default: null },
        addHref: { type: String, default: "/agencies/create" },
        // "top" = variant A: inline facets + More filters button that
        //   opens the FilterRail as a sheet.
        // "rail" = variant B: facets handled by the always-on FilterRail
        //   on the left, so we hide the facet/More-filters chrome.
        variant: { type: String, default: "top", validator: (v) => ["top", "rail"].includes(v) },
        // Facet descriptors — { slug, label, value, active }. Parent builds
        // these from filters; e.g. filters.service.length → value:
        // "3 selected", active: true.
        facets: { type: Array, default: () => [] },
        // Total number of active filters across the rail (for the
        // "More filters · 9" badge).
        activeFilterCount: { type: Number, default: 0 },
    },
    computed: {
        rootStyle() {
            return {
                padding: "18px 24px 12px",
                borderBottom: "1px solid var(--border-faint)",
                background: "var(--surface)",
                flex: "0 0 auto",
            };
        },
        titleRowStyle() {
            return {
                display: "flex",
                alignItems: "flex-end",
                gap: "16px",
                marginBottom: "14px",
            };
        },
        titleColStyle() {
            return { flex: "1 1 auto" };
        },
        titleLineStyle() {
            return {
                display: "flex",
                alignItems: "baseline",
                gap: "10px",
                marginBottom: "2px",
            };
        },
        h1Style() {
            return {
                font: "600 22px/1.1 var(--font-display)",
                letterSpacing: "-0.02em",
                margin: 0,
            };
        },
        countStyle() {
            return { fontSize: "13px", color: "var(--text-faint)" };
        },
        statsStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "14px",
                fontSize: "12px",
                color: "var(--text-muted)",
            };
        },
        statSpanStyle() {
            return {};
        },
        statValueStyle() {
            return { color: "var(--text)" };
        },
        actionsStyle() {
            return { display: "flex", alignItems: "center", gap: "8px" };
        },
        facetRowStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
            };
        },
        searchWrapStyle() {
            return { position: "relative", flex: "0 0 280px" };
        },
        searchIconStyle() {
            return {
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-faint)",
                display: "inline-flex",
            };
        },
        searchInputStyle() {
            return { height: "32px", paddingLeft: "32px", fontSize: "13px" };
        },
        searchKbdStyle() {
            return {
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "inline-flex",
                gap: "2px",
            };
        },
        statusRowStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
            };
        },
        statusEyebrowStyle() {
            return {
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-faint)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginRight: "4px",
            };
        },
        moreFiltersBtnStyle() {
            // Active state mirrors AgenciesHeader's variant-A button —
            // dark fill when the sheet is open, white when closed. The
            // parent owns the open-state, so we read it via prop later
            // if we want the visual toggle.
            return {
                gap: "6px",
                background: "var(--surface)",
                color: "var(--text)",
                borderColor: "var(--border-strong)",
            };
        },
        moreFiltersBadgeStyle() {
            return {
                fontSize: "11px",
                opacity: 0.7,
                marginLeft: "2px",
            };
        },
    },
    methods: {
        statusTabStyle(active) {
            return {
                height: "26px",
                padding: "0 9px",
                gap: "6px",
                background: active ? "var(--text)" : "transparent",
                color: active ? "var(--text-inv)" : "var(--text-muted)",
                borderColor: active ? "var(--text)" : "var(--border)",
                fontWeight: active ? 500 : 400,
            };
        },
        statusCountStyle(active) {
            return {
                fontSize: "10.5px",
                color: active
                    ? "color-mix(in oklch, var(--text-inv) 70%, transparent)"
                    : "var(--text-faint)",
            };
        },
        onSearchInput(event) {
            this.$emit("search", event.target.value);
        },
        onStatusClick(id) {
            // null means "All" — clear the selection in the parent.
            this.$emit("status-click", id);
        },
    },
};
</script>
